import { useEffect, useState } from "react";
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent,
  Divider, MenuItem, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, TextField, Tooltip, Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import {
  priceExplainApi,
  type ItemPickerRow, type ItemPriceCandidate, type PriceListType,
} from "@/api/endpoints";
import { errorMessage } from "@/api/client";
import { CatalogItemPicker } from "@/components/Pickers";
import { ClosableDialogTitle } from "@/components/ClosableDialogTitle";
import { StatCard } from "@/components/StatCard";
import { StatusPill } from "@/components/StatusPill";
import { derivationLabel } from "@/components/DerivationFields";
import { todayIso } from "@/shared/datetime";

const today = todayIso;
const money = (n?: number | null) =>
  n == null ? "—" : n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const SOURCE_LABEL: Record<string, string> = {
  PriceList: "Price list",
  BatchMrp: "Batch MRP",
  ItemDefaultMrp: "Item default MRP",
  None: "Nothing priced it",
};

/**
 * W17 — "why does this item cost this", for pharmacy.
 *
 * <p>The service charge master has had this since it was built, and it is the first thing support
 * reaches for. Item pricing is the harder half to reason about — a price list, a rate card ranking
 * above it, a derivation on top of that, an MRP ceiling underneath, and a feature flag that can
 * switch the whole chain off — and it had nothing.</p>
 *
 * <p><b>The losing candidates are the substance of this screen.</b> The winning price is the easy
 * part. What costs an afternoon is a corporate rate that exists, reads correctly, and is not being
 * used, because its card is still a draft or its window closed last month. Every line for the item
 * is listed with the reason it did or did not count.</p>
 */
export function ItemPriceExplainDialog({
  open, onClose, defaultItem,
}: {
  open: boolean;
  onClose: () => void;
  /** Pre-selects an item, when opened from somewhere that already knows which one. */
  defaultItem?: ItemPickerRow | null;
}) {
  const [item, setItem] = useState<ItemPickerRow | null>(null);
  const [context, setContext] = useState<PriceListType>("OP");
  const [onDate, setOnDate] = useState(today);

  useEffect(() => {
    if (!open) return;
    setItem(defaultItem ?? null);
    setOnDate(today());
    setContext("OP");
  }, [open, defaultItem]);

  const q = useQuery({
    queryKey: ["price-explain", item?.id, context, onDate],
    queryFn: () => priceExplainApi.item({ itemId: item!.id, context, onDate }),
    enabled: open && !!item,
  });

  const r = q.data;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <ClosableDialogTitle onClose={onClose}>Check a price</ClosableDialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Answers the question the counter asks: for this item, this patient, on this date — what
          would billing charge, and why did every other rate lose?
        </Typography>

        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
          <Box sx={{ flex: 2 }}>
            <CatalogItemPicker
              value={item} onChange={setItem}
              label="Item" placeholder="Search item…"
            />
          </Box>
          <TextField select size="small" label="Bill type" sx={{ minWidth: 140 }}
            value={context} onChange={(e) => setContext(e.target.value as PriceListType)}
            helperText="Which list tier applies">
            <MenuItem value="OP">Out-patient</MenuItem>
            <MenuItem value="IP">In-patient</MenuItem>
          </TextField>
          <TextField type="date" size="small" label="On date" sx={{ minWidth: 170 }}
            value={onDate} onChange={(e) => setOnDate(e.target.value)}
            InputLabelProps={{ shrink: true }} />
        </Stack>

        {!item && (
          <Alert severity="info" sx={{ mt: 3 }}>Pick an item to see how it would be priced.</Alert>
        )}
        {q.isLoading && (
          <Stack alignItems="center" sx={{ py: 4 }}><CircularProgress size={24} /></Stack>
        )}
        {q.isError && <Alert severity="error" sx={{ mt: 2 }}>{errorMessage(q.error)}</Alert>}

        {r && (
          <Box sx={{ mt: 3 }}>
            {/* The flag comes first because it makes everything below it moot. A tenant that has
                never switched the engine on will see a perfectly good price list ignored, and
                nothing else on this screen would explain why. */}
            {!r.priceListsEnabled && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <b>Price lists are switched off for this tenant.</b> Every line below is ignored at
                billing time, whatever it says — stock bills at batch MRP until{" "}
                <code>pricing.price_list</code> is enabled under Administration → Feature flags.
              </Alert>
            )}

            <Stack direction="row" spacing={2} sx={{ mb: 2, flexWrap: "wrap" }}>
              <StatCard label="Would bill at" value={money(r.rate)}
                severity={r.rate == null ? "critical" : "info"}
                hint={SOURCE_LABEL[r.source] ?? r.source} />
              <StatCard label="MRP ceiling" value={money(r.mrpCeiling)}
                hint={r.batchNo ? `batch ${r.batchNo}` : "highest the item can be sold at"} />
              <StatCard label="Candidates" value={r.candidates.length}
                hint={`${r.candidates.filter(c => c.eligible).length} in the running`} />
            </Stack>

            <Alert severity={r.problem ? "error" : "success"} sx={{ mb: 2 }}>
              {r.explanation}
              {r.problem && <Box sx={{ mt: 0.5 }}><b>{r.problem}</b></Box>}
            </Alert>

            <Divider sx={{ mb: 2 }} />
            <Typography variant="overline" color="text.secondary">
              Every price-list line for this item
            </Typography>

            {r.candidates.length === 0 ? (
              <Alert severity="info" sx={{ mt: 1 }}>
                No price list mentions this item at all, so it bills at MRP. That is the ordinary
                arrangement for a cash counter — a contracted rate is what would change it.
              </Alert>
            ) : (
              <CandidateTable candidates={r.candidates} />
            )}

            <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
              <Typography variant="overline" color="text.secondary">The fallbacks under it</Typography>
              <Stack spacing={0.5} sx={{ mt: 1 }}>
                <FallbackRow label="Batch MRP"
                  value={r.batchNo ? `${money(r.batchMrp)} — batch ${r.batchNo}` : "no batch in context"}
                  used={r.source === "BatchMrp"} />
                <FallbackRow label="Item default MRP" value={money(r.itemDefaultMrp)}
                  used={r.source === "ItemDefaultMrp"} />
              </Stack>
            </Paper>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

function FallbackRow({ label, value, used }: { label: string; value: string; used: boolean }) {
  return (
    <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography variant="body2" color="text.secondary">{label}</Typography>
        {used && <StatusPill label="used" tone="success" />}
      </Stack>
      <Typography variant="body2">{value}</Typography>
    </Stack>
  );
}

/**
 * The candidates, winner first. An ineligible row keeps its rate on screen deliberately: "there is
 * a 180.00 corporate rate here and it is not being used" is the sentence somebody needs to read.
 */
function CandidateTable({ candidates }: { candidates: ItemPriceCandidate[] }) {
  return (
    <TableContainer sx={{ maxHeight: 340, mt: 1 }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: 90 }}>Outcome</TableCell>
            <TableCell>Price list</TableCell>
            <TableCell>Rate card</TableCell>
            <TableCell sx={{ width: 170 }}>Effective</TableCell>
            <TableCell align="right" sx={{ width: 110 }}>Rate</TableCell>
            <TableCell sx={{ width: 260 }}>Why</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {candidates.map(c => (
            <TableRow key={`${c.priceListId}-${c.effectiveFrom}-${c.tariffId ?? ""}`} hover
              sx={{ opacity: c.eligible ? 1 : 0.65 }}>
              <TableCell>
                {c.won ? <StatusPill label="Wins" tone="success" />
                  : c.eligible ? <StatusPill label="In run" tone="info" />
                  : <StatusPill label="Ignored" tone="neutral" />}
              </TableCell>
              <TableCell>
                <Stack spacing={0}>
                  <b>{c.listCode}</b>
                  <Typography variant="caption" color="text.secondary">
                    {c.listType} · priority {c.listPriority}
                    {c.tier != null ? ` · tier ${c.tier}` : ""}
                  </Typography>
                </Stack>
              </TableCell>
              <TableCell>
                {c.tariffCode ? (
                  <Tooltip title={`Card priority ${c.tariffPriority ?? 0} — outranks the list tier`}>
                    <Stack spacing={0}>
                      <StatusPill label={c.tariffCode}
                        tone={c.tariffStatus === "Active" ? "success" : "warning"} />
                      <Typography variant="caption" color="text.secondary">
                        {c.tariffStatus}
                      </Typography>
                    </Stack>
                  </Tooltip>
                ) : (
                  <Box component="span" sx={{ color: "text.disabled" }}>—</Box>
                )}
              </TableCell>
              <TableCell>
                <Typography variant="body2">
                  {c.effectiveFrom} → {c.effectiveTo ?? "open"}
                </Typography>
              </TableCell>
              <TableCell align="right">
                <Stack spacing={0} alignItems="flex-end">
                  <span>{money(c.rate)}</span>
                  {derivationLabel(c) && (
                    <Typography variant="caption" color="text.secondary">
                      {derivationLabel(c)}
                    </Typography>
                  )}
                </Stack>
              </TableCell>
              <TableCell>
                {c.ineligibleReason
                  ? <Typography variant="caption" color="warning.main">{c.ineligibleReason}</Typography>
                  : c.won
                  ? <Typography variant="caption" color="success.main">
                      Highest-ranked line covering this item today.
                    </Typography>
                  : <Typography variant="caption" color="text.secondary">
                      Eligible, but outranked.
                    </Typography>}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
